/* Audio engine: plucked guitar (Karplus-Strong), soft piano, sine, bass, drums, and a
   look-ahead player for sequences, chords and progressions. The AudioContext is created lazily. */
import { QUAL } from '../dsp/chord';
import { rng } from '../dsp/rng';
import { ksPluck, pianoTone } from '../dsp/synth';
import { getSettings, storeEvents } from '../store/state';
import { type ChordRef, chordInKey, type Mode, mod, parseChord, parsePitch, voicing } from '../theory';

export type Voice = 'guitar' | 'piano' | 'sine' | 'bass';
export type DrumKind = 'kick' | 'snare' | 'hat' | 'click' | 'clickHi';
export type ChordInput = string | ChordRef;

export type Cue =
  | { type: 'note'; i: number; midi: number }
  | { type: 'bar'; index: number; chord: ChordInput | null }
  | { type: 'count'; n: number }
  | { type: 'beat'; k: number };

interface EventBase {
  /** seconds from the start of the sequence */
  t: number;
  cue?: Cue;
  noGate?: boolean;
}
export interface NoteEvent extends EventBase {
  kind: 'note';
  midi: number;
  dur: number;
  inst?: Voice;
  vel?: number;
  release?: number;
  variant?: number;
}
export interface StrumEvent extends EventBase {
  kind: 'strum';
  midis: number[];
  dur?: number;
  dir?: 'up' | 'down';
  vel?: number;
  spread?: number;
  inst?: Voice;
  release?: number;
}
export interface DrumEvent extends EventBase {
  kind: 'drum';
  drum: DrumKind;
  vel?: number;
}
export interface GlideEvent extends EventBase {
  kind: 'glide';
  points: [number, number][];
  wave?: OscillatorType;
  vel?: number;
}
/** Vibrato over part of a sung line: seconds from the line's start, rate in Hz, depth in ± cents. */
export interface VibSpan {
  from: number;
  to: number;
  rate: number;
  depth: number;
}
/** A sung line: one continuous voice-like tone following a pitch contour (melisma, glissando, vibrato). */
export interface LineEvent extends EventBase {
  kind: 'line';
  /** [tSec, midi] from the line's start; the pitch moves evenly in semitones between points */
  points: [number, number][];
  vib?: VibSpan[];
  vel?: number;
}
export interface CueEvent extends EventBase {
  kind?: undefined;
  cue: Cue;
}
export type AudioEvent = NoteEvent | StrumEvent | DrumEvent | GlideEvent | LineEvent | CueEvent;

export interface Sequence {
  ev: AudioEvent[];
  length: number;
}

export interface PlayOptions {
  /** Total length in seconds; defaults to the end of the last event. */
  length?: number;
  delay?: number;
  loop?: boolean;
  tail?: number;
  /** Do not pause microphone analysis while this plays (play-along, headphones). */
  noGate?: boolean;
  onCue?: (cue: Cue, at: number) => void;
  onEnd?: (stopped: boolean) => void;
}

interface VoiceNode {
  src: AudioScheduledSourceNode;
  g: GainNode;
  stopAt: number;
}

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noise: AudioBuffer | null = null;
const buses: Partial<Record<Voice | 'drums', AudioNode>> = {};
const active = new Set<VoiceNode>();
const players = new Set<Player>();
let collector: VoiceNode[] | null = null;
/** Until this AudioContext time the app is making sound; the mic ignores input meanwhile. */
let busyUntil = 0;
/** Final output stage (after the compressor), where the debug tap listens. */
let output: AudioNode | null = null;
let tap: AnalyserNode | null = null;
let voiceWave: PeriodicWave | null = null;

function impulse(c: AudioContext, sec: number, decay: number): AudioBuffer {
  const len = Math.floor(c.sampleRate * sec);
  const b = c.createBuffer(2, len, c.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = b.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** decay;
  }
  return b;
}

function init(): AudioContext {
  const AC =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) throw new Error('Bu tarayıcı Web Audio desteklemiyor.');
  const c = new AC({ latencyHint: 'interactive' });
  ctx = c;
  master = c.createGain();
  master.gain.value = getSettings().volume;
  const comp = c.createDynamicsCompressor();
  comp.threshold.value = -16;
  comp.knee.value = 10;
  comp.ratio.value = 3.5;
  comp.attack.value = 0.003;
  comp.release.value = 0.25;
  master.connect(comp);
  comp.connect(c.destination);
  output = comp;
  const verb = c.createConvolver();
  verb.buffer = impulse(c, 1.8, 2.6);
  const verbIn = c.createGain();
  verbIn.gain.value = 0.2;
  verbIn.connect(verb);
  verb.connect(master);
  const gIn = c.createGain();
  const body = c.createBiquadFilter();
  body.type = 'peaking';
  body.frequency.value = 200;
  body.Q.value = 1.0;
  body.gain.value = 3.5;
  const air = c.createBiquadFilter();
  air.type = 'highshelf';
  air.frequency.value = 3500;
  air.gain.value = -4;
  gIn.connect(body);
  body.connect(air);
  air.connect(master);
  air.connect(verbIn);
  buses.guitar = gIn;
  const pIn = c.createGain();
  pIn.connect(master);
  pIn.connect(verbIn);
  buses.piano = pIn;
  buses.sine = pIn;
  const bIn = c.createGain();
  const blp = c.createBiquadFilter();
  blp.type = 'lowpass';
  blp.frequency.value = 900;
  bIn.connect(blp);
  blp.connect(master);
  buses.bass = bIn;
  const dIn = c.createGain();
  dIn.gain.value = 0.75;
  dIn.connect(master);
  buses.drums = dIn;
  return c;
}

const getCtx = (): AudioContext => ctx || init();

storeEvents.on('settings', (e) => {
  if (e.key === 'volume' && master && ctx) master.gain.setTargetAtTime(e.val as number, ctx.currentTime, 0.03);
});

// ------------------------------------------------------------ sample cache
const cache = new Map<string, { buf: AudioBuffer; rate: number }>();
function getBuf(inst: Voice, midi: number, variant: number): { buf: AudioBuffer; rate: number } {
  const key = `${inst}:${midi}:${variant}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const c = getCtx();
  const sr = c.sampleRate;
  const f = 440 * 2 ** ((midi - 69) / 12);
  let res: ReturnType<typeof ksPluck>;
  if (inst === 'guitar') {
    res = ksPluck(f, sr, midi < 50 ? 3.4 : midi < 64 ? 2.8 : 2.2, {
      rand: rng(midi * 977 + variant * 131 + 7),
      brightness: 0.45 + 0.1 * variant,
      pickPos: 0.13 + 0.05 * variant,
    });
  } else if (inst === 'bass') {
    res = ksPluck(f, sr, 2.2, { rand: rng(midi * 31 + 5), brightness: 0.3, pickPos: 0.25, t60: 2.4 });
  } else {
    res = pianoTone(f, sr, midi < 60 ? 3.2 : 2.4);
  }
  const buf = c.createBuffer(1, res.data.length, sr);
  buf.getChannelData(0).set(res.data);
  const e = { buf, rate: f / res.actualFreq };
  cache.set(key, e);
  return e;
}

function register(node: VoiceNode): void {
  active.add(node);
  node.src.onended = () => {
    active.delete(node);
    try {
      node.g.disconnect();
    } catch {
      /* already disconnected */
    }
  };
  if (collector) collector.push(node);
}

// ------------------------------------------------------------ voices
export interface NoteOptions {
  inst?: Voice;
  vel?: number;
  release?: number;
  variant?: number;
  noGate?: boolean;
}

export function note(midi: number, at: number, dur: number, o: NoteOptions = {}): VoiceNode {
  const c = getCtx();
  const inst: Voice = o.inst || getSettings().instrument || 'guitar';
  const start = Math.max(at || 0, c.currentTime);
  const length = Math.max(0.05, dur || 1);
  const vel = o.vel == null ? 0.8 : o.vel;
  const rel = o.release == null ? 0.12 : o.release;
  const end = start + length;
  const tune = (getSettings().a4 || 440) / 440;
  const g = c.createGain();
  let src: AudioScheduledSourceNode;
  let peak: number;
  if (inst === 'sine') {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 440 * 2 ** ((midi - 69) / 12) * tune;
    src = osc;
    peak = 0.32 * vel;
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(peak, start + 0.02);
  } else {
    const variant = inst === 'guitar' ? (o.variant != null ? o.variant : Math.floor(Math.random() * 3)) : 0;
    const b = getBuf(inst, midi, variant);
    const bs = c.createBufferSource();
    bs.buffer = b.buf;
    bs.playbackRate.value = b.rate * tune;
    src = bs;
    peak = vel * (inst === 'piano' ? 0.42 : inst === 'bass' ? 0.75 : 0.6);
    g.gain.setValueAtTime(peak, start);
  }
  g.gain.setValueAtTime(peak, end);
  g.gain.setTargetAtTime(0, end, rel / 3);
  src.connect(g);
  g.connect((buses[inst] || buses.guitar) as AudioNode);
  src.start(start);
  const stopAt = end + rel * 3 + 0.05;
  src.stop(stopAt);
  const node = { src, g, stopAt };
  register(node);
  if (!o.noGate) busyUntil = Math.max(busyUntil, end + rel + 0.25);
  return node;
}

export interface StrumOptions {
  dir?: 'up' | 'down';
  spread?: number;
  vel?: number;
  dur?: number;
  inst?: Voice;
  release?: number;
  noGate?: boolean;
}

export function strum(midis: readonly number[], at: number, o: StrumOptions = {}): void {
  const dir = o.dir || 'down';
  const spread = o.spread == null ? 0.016 : o.spread;
  const list = dir === 'up' ? midis.slice().reverse() : midis.slice();
  const vel = o.vel == null ? 0.75 : o.vel;
  const dur = o.dur == null ? 1.6 : o.dur;
  list.forEach((m, i) => {
    const t = at + i * spread + (Math.random() - 0.5) * 0.004;
    note(m, t, Math.max(0.08, dur - i * spread), {
      inst: o.inst === 'piano' ? 'piano' : 'guitar',
      vel: 0.75 * vel * (dir === 'up' ? 0.7 : 1) * (0.85 + Math.random() * 0.25),
      release: o.release == null ? 0.08 : o.release,
      noGate: o.noGate,
    });
  });
}

function noiseBuffer(c: AudioContext): AudioBuffer {
  if (noise) return noise;
  const len = Math.floor(c.sampleRate * 0.6);
  noise = c.createBuffer(1, len, c.sampleRate);
  const d = noise.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return noise;
}

export function drum(kind: DrumKind, at: number, vel = 1): void {
  const c = getCtx();
  const start = Math.max(at || 0, c.currentTime);
  const out = buses.drums as AudioNode;
  const made: VoiceNode[] = [];
  if (kind === 'kick') {
    const o = c.createOscillator();
    const g = c.createGain();
    o.frequency.setValueAtTime(150, start);
    o.frequency.exponentialRampToValueAtTime(45, start + 0.12);
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(0.9 * vel, start + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, start + 0.32);
    o.connect(g);
    g.connect(out);
    o.start(start);
    o.stop(start + 0.35);
    made.push({ src: o, g, stopAt: start + 0.35 });
  } else if (kind === 'snare' || kind === 'hat') {
    const s = c.createBufferSource();
    s.buffer = noiseBuffer(c);
    const f = c.createBiquadFilter();
    const g = c.createGain();
    if (kind === 'hat') {
      f.type = 'highpass';
      f.frequency.value = 7000;
      g.gain.setValueAtTime(0.16 * vel, start);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.05);
    } else {
      f.type = 'bandpass';
      f.frequency.value = 1800;
      f.Q.value = 0.7;
      g.gain.setValueAtTime(0.42 * vel, start);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.16);
      const o = c.createOscillator();
      const og = c.createGain();
      o.frequency.setValueAtTime(190, start);
      o.frequency.exponentialRampToValueAtTime(140, start + 0.08);
      og.gain.setValueAtTime(0.28 * vel, start);
      og.gain.exponentialRampToValueAtTime(0.001, start + 0.1);
      o.connect(og);
      og.connect(out);
      o.start(start);
      o.stop(start + 0.12);
      made.push({ src: o, g: og, stopAt: start + 0.12 });
    }
    s.connect(f);
    f.connect(g);
    g.connect(out);
    s.start(start, Math.random() * 0.3);
    s.stop(start + 0.2);
    made.push({ src: s, g, stopAt: start + 0.2 });
  } else {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = 'sine';
    o.frequency.value = kind === 'clickHi' ? 1760 : 1175;
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(0.45 * vel, start + 0.002);
    g.gain.exponentialRampToValueAtTime(0.001, start + 0.05);
    o.connect(g);
    g.connect(out);
    o.start(start);
    o.stop(start + 0.06);
    made.push({ src: o, g, stopAt: start + 0.06 });
  }
  made.forEach(register);
}

/** Continuous pitch glide (siren guide). points: [[tSec, midi], ...] */
export function glideAt(
  points: [number, number][],
  at: number,
  o: { wave?: OscillatorType; vel?: number; noGate?: boolean } = {},
): void {
  const c = getCtx();
  const start = Math.max(at || 0, c.currentTime);
  const osc = c.createOscillator();
  osc.type = o.wave || 'triangle';
  const g = c.createGain();
  const tune = (getSettings().a4 || 440) / 440;
  points.forEach(([t, m], i) => {
    const f = 440 * 2 ** ((m - 69) / 12) * tune;
    if (i === 0) osc.frequency.setValueAtTime(f, start + t);
    else osc.frequency.linearRampToValueAtTime(f, start + t);
  });
  const end = start + (points[points.length - 1] as [number, number])[0];
  const vel = o.vel || 0.2;
  g.gain.setValueAtTime(0, start);
  g.gain.linearRampToValueAtTime(vel, start + 0.08);
  g.gain.setValueAtTime(vel, end);
  g.gain.linearRampToValueAtTime(0, end + 0.15);
  osc.connect(g);
  g.connect(buses.sine as AudioNode);
  osc.start(start);
  osc.stop(end + 0.2);
  register({ src: osc, g, stopAt: end + 0.2 });
  if (!o.noGate) busyUntil = Math.max(busyUntil, end + 0.35);
}

/** Harmonic spectrum of a sung "aa": falling partials with a stronger 2nd and 3rd, softened by a low-pass. */
function getVoiceWave(c: AudioContext): PeriodicWave {
  if (voiceWave) return voiceWave;
  const n = 20;
  const real = new Float32Array(n);
  const imag = new Float32Array(n);
  for (let h = 1; h < n; h++) imag[h] = h ** -1.2 * (h === 2 || h === 3 ? 1.4 : 1);
  voiceWave = c.createPeriodicWave(real, imag);
  return voiceWave;
}

/** Sung line (see LineEvent): the contour drives the oscillator's detune, vibrato adds an LFO on top. */
export function lineAt(
  points: readonly [number, number][],
  at: number,
  o: { vib?: readonly VibSpan[]; vel?: number; noGate?: boolean } = {},
): void {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return;
  const c = getCtx();
  const start = Math.max(at || 0, c.currentTime);
  const end = start + last[0];
  const base = first[1];
  const osc = c.createOscillator();
  osc.setPeriodicWave(getVoiceWave(c));
  osc.frequency.value = 440 * 2 ** ((base - 69) / 12) * ((getSettings().a4 || 440) / 440);
  points.forEach(([t, m], i) => {
    const cents = (m - base) * 100;
    if (i === 0) osc.detune.setValueAtTime(cents, start + t);
    else osc.detune.linearRampToValueAtTime(cents, start + t);
  });
  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2800;
  lp.Q.value = 0.5;
  const g = c.createGain();
  const vel = o.vel ?? 0.24;
  g.gain.setValueAtTime(0, start);
  g.gain.linearRampToValueAtTime(vel, start + 0.05);
  g.gain.setValueAtTime(vel, end);
  g.gain.linearRampToValueAtTime(0, end + 0.12);
  osc.connect(lp);
  lp.connect(g);
  g.connect(buses.sine as AudioNode);
  osc.start(start);
  osc.stop(end + 0.2);
  register({ src: osc, g, stopAt: end + 0.2 });
  if (o.vib?.length) {
    const lfo = c.createOscillator();
    const depth = c.createGain();
    depth.gain.setValueAtTime(0, start);
    for (const v of o.vib) {
      // singers' vibrato sets in shortly after the note starts
      const onset = Math.min(0.3, (v.to - v.from) / 3);
      lfo.frequency.setValueAtTime(v.rate, start + v.from);
      depth.gain.setValueAtTime(0, start + v.from);
      depth.gain.linearRampToValueAtTime(v.depth, start + v.from + onset);
      depth.gain.setValueAtTime(v.depth, start + Math.max(v.from + onset, v.to - 0.04));
      depth.gain.linearRampToValueAtTime(0, start + v.to);
    }
    lfo.connect(depth);
    depth.connect(osc.detune);
    lfo.start(start);
    lfo.stop(end + 0.2);
    register({ src: lfo, g: depth, stopAt: end + 0.2 });
  }
  if (!o.noGate) busyUntil = Math.max(busyUntil, end + 0.4);
}

/** End of an event in seconds from the sequence start. */
const eventEnd = (e: AudioEvent): number =>
  e.t + ('dur' in e && e.dur ? e.dur : e.kind === 'line' || e.kind === 'glide' ? (e.points.at(-1)?.[0] ?? 0) : 0);

// ------------------------------------------------------------ player
export class Player {
  readonly o: PlayOptions;
  readonly events: AudioEvent[];
  readonly length: number;
  readonly done: Promise<boolean>;
  t0: number;
  stopped = false;
  finished = false;
  loops = 0;
  private i = 0;
  private nodes: VoiceNode[] = [];
  private timers: ReturnType<typeof setTimeout>[] = [];
  private timer: ReturnType<typeof setInterval>;
  private resolveDone!: (completed: boolean) => void;

  constructor(events: AudioEvent[], o: PlayOptions = {}) {
    const c = getCtx();
    this.o = o;
    this.events = events.slice().sort((a, b) => a.t - b.t);
    this.length = o.length != null ? o.length : this.events.reduce((m, e) => Math.max(m, eventEnd(e)), 0);
    this.t0 = c.currentTime + (o.delay == null ? 0.06 : o.delay);
    this.done = new Promise((r) => {
      this.resolveDone = r;
    });
    players.add(this);
    this.timer = setInterval(() => this.tick(), 25);
    this.tick();
  }

  /** Seconds since the sequence started (negative during the start delay). */
  get time(): number {
    return getCtx().currentTime - this.t0;
  }

  private tick(): void {
    if (this.stopped) return;
    const now = getCtx().currentTime;
    const horizon = now + 0.2;
    while (this.i < this.events.length && this.t0 + (this.events[this.i] as AudioEvent).t < horizon) {
      const ev = this.events[this.i++] as AudioEvent;
      this.fire(ev, this.t0 + ev.t);
    }
    if (this.i >= this.events.length) {
      if (this.o.loop) {
        if (this.t0 + this.length < horizon) {
          this.t0 += this.length;
          this.i = 0;
          this.loops++;
        }
      } else if (now >= this.t0 + this.length + (this.o.tail == null ? 0.05 : this.o.tail)) {
        this.finish(false);
      }
    }
  }

  private fire(ev: AudioEvent, at: number): void {
    collector = this.nodes;
    const noGate = ev.noGate || this.o.noGate;
    try {
      if (ev.kind === 'note') note(ev.midi, at, ev.dur, { ...ev, noGate });
      else if (ev.kind === 'strum') strum(ev.midis, at, { ...ev, noGate });
      else if (ev.kind === 'drum') drum(ev.drum, at, ev.vel);
      else if (ev.kind === 'glide') glideAt(ev.points, at, { ...ev, noGate });
      else if (ev.kind === 'line') lineAt(ev.points, at, { ...ev, noGate });
    } finally {
      collector = null;
    }
    const cue = ev.cue;
    if (cue && this.o.onCue) {
      const delay = Math.max(0, (at - getCtx().currentTime) * 1000);
      this.timers.push(
        setTimeout(() => {
          if (!this.stopped) this.o.onCue?.(cue, at);
        }, delay),
      );
    }
    if (this.nodes.length > 300) {
      const now = getCtx().currentTime;
      this.nodes = this.nodes.filter((n) => n.stopAt > now);
    }
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    clearInterval(this.timer);
    this.timers.forEach(clearTimeout);
    const now = getCtx().currentTime;
    for (const n of this.nodes) silence(n, now);
    if (!Array.from(players).some((p) => p !== this && !p.stopped)) busyUntil = Math.min(busyUntil, now + 0.25);
    this.finish(true);
  }

  private finish(stopped: boolean): void {
    if (this.finished) return;
    this.finished = true;
    clearInterval(this.timer);
    players.delete(this);
    try {
      this.o.onEnd?.(stopped);
    } catch (e) {
      console.error(e);
    }
    this.resolveDone(!stopped);
  }
}

function silence(n: VoiceNode, now: number): void {
  try {
    n.g.gain.cancelScheduledValues(now);
    n.g.gain.setTargetAtTime(0, now, 0.02);
    n.src.stop(now + 0.12);
  } catch {
    /* already stopped */
  }
}

export const play = (events: AudioEvent[], o?: PlayOptions): Player => new Player(events, o);

export function stopAll(): void {
  for (const p of Array.from(players)) p.stop();
  if (!ctx) return;
  const now = ctx.currentTime;
  for (const n of Array.from(active)) silence(n, now);
  busyUntil = Math.min(busyUntil, now + 0.2);
}

export const anyPlaying = (): boolean => Array.from(players).some((p) => !p.stopped);

// ------------------------------------------------------------ builders
export type SeqItem = number | null | { midi: number; beats: number };

export interface SeqOptions {
  bpm?: number;
  start?: number;
  legato?: number;
  inst?: Voice;
  vel?: number;
}

/** items: midi | null (rest) | {midi, beats} */
export function seqEvents(items: readonly SeqItem[], o: SeqOptions = {}): Sequence {
  const beat = 60 / (o.bpm || 90);
  const ev: AudioEvent[] = [];
  let t = o.start || 0;
  items.forEach((it, i) => {
    const obj = it !== null && typeof it === 'object';
    const m = obj ? it.midi : it;
    const beats = obj ? it.beats || 1 : 1;
    if (m != null)
      ev.push({
        kind: 'note',
        midi: m,
        t,
        dur: beats * beat * (o.legato || 0.92),
        inst: o.inst,
        vel: o.vel,
        cue: { type: 'note', i, midi: m },
      });
    t += beats * beat;
  });
  return { ev, length: t };
}

export function playNotes(items: readonly SeqItem[], o: SeqOptions & PlayOptions = {}): Player {
  const s = seqEvents(items, o);
  return play(s.ev, { length: s.length, ...o });
}

export const playNote = (midi: number, o: { dur?: number; inst?: Voice; vel?: number } & PlayOptions = {}): Player =>
  play([{ kind: 'note', midi, t: 0, dur: o.dur || 1.2, inst: o.inst, vel: o.vel }], o);

export const playHarm = (midis: readonly number[], o: { dur?: number; inst?: Voice } & PlayOptions = {}): Player =>
  play(
    midis.map((m) => ({ kind: 'note', midi: m, t: 0, dur: o.dur || 1.8, inst: o.inst, vel: 0.6 })),
    o,
  );

function toChord(chord: ChordInput): ChordRef | null {
  return typeof chord === 'string' ? parseChord(chord) : chord;
}

function pianoVoicing(chord: ChordInput): number[] {
  const c = toChord(chord);
  if (!c) return [];
  const root = 48 + mod(c.root, 12);
  const out = [root - 12 >= 36 ? root - 12 : root];
  for (const iv of QUAL[c.q]) out.push(root + iv);
  return out;
}

export function chordEvents(
  chord: ChordInput,
  t: number,
  dur: number,
  o: { inst?: Voice; vel?: number; spread?: number } = {},
): AudioEvent[] {
  if (o.inst === 'piano')
    return pianoVoicing(chord).map((m) => ({ kind: 'note', midi: m, t, dur, inst: 'piano', vel: 0.55 }));
  return [{ kind: 'strum', midis: voicing(chord), t, dur, dir: 'down', vel: o.vel || 0.8, spread: o.spread }];
}

export function playChord(chord: ChordInput, o: { dur?: number; inst?: Voice } & PlayOptions = {}): Player {
  const dur = o.dur || 2.2;
  return play(chordEvents(chord, 0, dur, o), { length: dur, ...o });
}

export interface ProgOptions {
  bpm?: number;
  start?: number;
  beats?: number;
  beatsList?: number[];
  /** Strum pattern in eighth-note slots: D = down, U = up, - = rest. */
  pattern?: string;
  inst?: Voice;
  bass?: boolean;
  drums?: boolean;
  click?: boolean;
  noChords?: boolean;
  noGate?: boolean;
  chordVel?: number;
}

/** Progression: one chord per bar (or o.beatsList per chord), strummed with a pattern. */
export function progEvents(chords: readonly (ChordInput | null)[], o: ProgOptions = {}): Sequence {
  const beat = 60 / (o.bpm || 90);
  const ev: AudioEvent[] = [];
  let t = o.start || 0;
  chords.forEach((c, ci) => {
    const beats = o.beatsList?.[ci] || o.beats || 4;
    let pat: string;
    if (o.pattern) {
      const per = o.pattern.length / (o.beats || 4);
      const n = Math.max(1, Math.round(per * beats));
      pat = o.pattern.repeat(Math.ceil(n / o.pattern.length)).slice(0, n);
    } else pat = beats === 3 ? 'D-DUDU' : beats === 2 ? 'D-DU' : beats === 1 ? 'D-' : 'D-DU-UDU';
    const slots = pat.length;
    const slotDur = (beats * beat) / slots;
    ev.push({ t, cue: { type: 'bar', index: ci, chord: c } });
    const parsed = c ? toChord(c) : null;
    if (parsed && !o.noChords) {
      const v = o.inst === 'piano' ? pianoVoicing(parsed) : voicing(parsed);
      const hits: number[] = [];
      for (let s = 0; s < slots; s++) if (pat[s] && pat[s] !== '-') hits.push(s);
      hits.forEach((s, k) => {
        const next = k + 1 < hits.length ? (hits[k + 1] as number) : slots;
        const dur = (next - s) * slotDur + 0.04;
        const dir = pat[s] === 'U' ? 'up' : 'down';
        const strong = s === 0 || (slots === 8 && s === 4);
        if (o.inst === 'piano')
          for (const m of v)
            ev.push({
              kind: 'note',
              midi: m,
              t: t + s * slotDur,
              dur,
              inst: 'piano',
              vel: strong ? 0.5 : 0.35,
              noGate: o.noGate,
            });
        else
          ev.push({
            kind: 'strum',
            midis: dir === 'up' ? v.slice(-4) : v,
            t: t + s * slotDur,
            dur,
            dir,
            vel: (strong ? 0.85 : 0.6) * (o.chordVel || 1),
            spread: dir === 'up' ? 0.01 : 0.016,
            noGate: o.noGate,
          });
      });
    }
    // the bass plays with or without the guitar ("Bas" alone, "Davul + bas"), and stays inside its bar
    if (parsed && o.bass) {
      const bm = 28 + mod(parsed.root - 4, 12);
      const steps = beats >= 4 ? [0, 2] : [0];
      for (const b of steps)
        ev.push({
          kind: 'note',
          inst: 'bass',
          midi: bm,
          t: t + b * beat,
          dur: beat * (beats === 3 ? 2.8 : Math.min(1.85, beats - 0.15)),
          vel: 0.9,
          noGate: o.noGate,
        });
    }
    if (o.drums) {
      for (let b = 0; b < beats; b++) {
        const bt = t + b * beat;
        if (beats === 3) ev.push({ kind: 'drum', drum: b === 0 ? 'kick' : 'hat', t: bt, vel: b === 0 ? 0.9 : 0.7 });
        else {
          ev.push({ kind: 'drum', drum: b % 2 === 0 ? 'kick' : 'snare', t: bt, vel: b % 2 === 0 ? 0.9 : 0.6 });
          ev.push({ kind: 'drum', drum: 'hat', t: bt + beat / 2, vel: 0.5 });
        }
      }
    }
    if (o.click)
      for (let b = 0; b < beats; b++)
        ev.push({ kind: 'drum', drum: b === 0 ? 'clickHi' : 'click', t: t + b * beat, vel: 0.7 });
    t += beats * beat;
  });
  return { ev, length: t };
}

export function playProg(chords: readonly (ChordInput | null)[], o: ProgOptions & PlayOptions = {}): Player {
  const s = progEvents(chords, o);
  return play(s.ev, { length: s.length, ...o });
}

export function countInEvents(beats: number, bpm: number): Sequence {
  const beat = 60 / bpm;
  const ev: AudioEvent[] = [];
  for (let b = 0; b < beats; b++)
    ev.push({
      kind: 'drum',
      drum: b === 0 ? 'clickHi' : 'click',
      t: b * beat,
      vel: 0.8,
      cue: { type: 'count', n: beats - b },
    });
  return { ev, length: beats * beat };
}

/** I–IV–V–I (or i–iv–V–i) cadence that establishes a key. */
export function cadenceEvents(tonic: number, mode: Mode, o: { bpm?: number; inst?: Voice } = {}): Sequence {
  const degs = mode === 'minor' ? ['i', 'iv', 'V', 'i'] : ['I', 'IV', 'V', 'I'];
  const beat = 60 / (o.bpm || 112);
  const ev: AudioEvent[] = [];
  let t = 0;
  degs.forEach((d, i) => {
    const c = chordInKey(d, tonic, mode);
    const dur = i === 3 ? beat * 2.6 : beat * 1.9;
    ev.push(...chordEvents({ root: c.root, q: c.q }, t, dur, o));
    t += beat * 2;
  });
  return { ev, length: t + beat * 0.6 };
}

export function playCadence(tonic: number, mode: Mode, o: { bpm?: number; inst?: Voice } & PlayOptions = {}): Player {
  const s = cadenceEvents(tonic, mode, o);
  return play(s.ev, { length: s.length, ...o });
}

/** Lesson play spec: exactly one of seq | harm | glide | chord | prog | cadence, plus optional tempo and voice.
    Notes may carry a cent offset ("E4-14c") for sounds between the keys. */
export interface PlaySpec {
  /** Space-separated notes, "-" for a rest, ":2" for beats: "G3 A3 B3:2" */
  seq?: string;
  /** Notes sounded together: "C4 E4" */
  harm?: string;
  /** A sung line (see glideEvents): "A3 B3:0.5 C4:0.5 /E4:2 E4:2~" */
  glide?: string;
  chord?: string;
  /** Space-separated chords, "|" bar lines are ignored: "C F G C" */
  prog?: string;
  /** Tonic chord whose key gets a cadence: "C" or "Am" */
  cadence?: string;
  bpm?: number;
  inst?: Voice;
  beats?: number;
  pattern?: string;
}

export function parseSeq(str: string): SeqItem[] {
  return String(str)
    .trim()
    .split(/\s+/)
    .map((tok) => {
      if (tok === '-' || tok === '_') return null;
      const [n, b] = tok.split(':');
      const m = parsePitch(n as string);
      if (m == null) return null;
      return b ? { midi: m, beats: Number.parseFloat(b) } : m;
    });
}

/** One step of a sung line. */
export interface GlideToken {
  /** fractional MIDI note, null for a breath (rest) */
  midi: number | null;
  beats: number;
  /** reach this pitch by sliding through the whole step (glissando) instead of moving at once */
  slide: boolean;
  vib: { rate: number; depth: number } | null;
}

/** Natural vibrato used by "~" without numbers: 5.5 Hz, ±35 cents. */
export const DEFAULT_VIBRATO = { rate: 5.5, depth: 35 } as const;

/** "/E4-14c:2~5/50" -> slide to E4 14 cents flat over 2 beats, with vibrato at 5 Hz, ±50 cents.
    Grammar: [/]note[±cents c][:beats][~[rate/depth]]; "-" is a breath. */
export function parseGlideToken(tok: string): GlideToken | null {
  const m = /^(\/)?([^:~/]+)(?::(\d+(?:\.\d+)?))?(~(?:(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?))?)?$/.exec(tok);
  if (!m) return null;
  const beats = m[3] ? Number.parseFloat(m[3]) : 1;
  if (!(beats > 0)) return null;
  if (m[2] === '-') return m[1] || m[4] ? null : { midi: null, beats, slide: false, vib: null };
  const midi = parsePitch(m[2] as string);
  if (midi == null) return null;
  const vib = m[4]
    ? m[5]
      ? { rate: Number.parseFloat(m[5]), depth: Number.parseFloat(m[6] as string) }
      : { ...DEFAULT_VIBRATO }
    : null;
  if (vib && !(vib.rate > 0 && vib.rate <= 12 && vib.depth > 0 && vib.depth <= 200)) return null;
  return { midi, beats, slide: !!m[1], vib };
}

/** Sung-line events for a glide spec. Each step holds its note; plain steps move to it in a quick
    legato (like a run), "/" steps slide the whole way, "-" takes a breath and starts a new line. */
export function glideEvents(str: string, o: { bpm?: number } = {}): Sequence {
  const beat = 60 / (o.bpm || 80);
  const ev: AudioEvent[] = [];
  let t = 0;
  let line: LineEvent | null = null;
  let prev: number | null = null;
  for (const tok of String(str).trim().split(/\s+/)) {
    const g = parseGlideToken(tok);
    if (!g) continue;
    const dur = g.beats * beat;
    if (g.midi == null) {
      line = null;
      prev = null;
    } else {
      if (!line) {
        line = { kind: 'line', t, points: [[0, g.midi]], vib: [] };
        ev.push(line);
      } else if (prev != null && prev !== g.midi) {
        const rel = t - line.t;
        if (g.slide) line.points.push([rel + dur, g.midi]);
        else line.points.push([rel + Math.min(0.06, dur * 0.4), g.midi]);
      }
      line.points.push([t + dur - line.t, g.midi]);
      if (g.vib) line.vib?.push({ from: t - line.t, to: t + dur - line.t, ...g.vib });
      prev = g.midi;
    }
    t += dur;
  }
  for (const e of ev) {
    if (e.kind !== 'line') continue;
    // drop points that repeat the previous time (a slide step already ends where its hold ends)
    e.points = e.points.filter((p, i, a) => i === 0 || p[0] > (a[i - 1] as [number, number])[0]);
    if (!e.vib?.length) delete e.vib;
  }
  return { ev, length: t };
}

export function specEvents(spec: PlaySpec): Sequence {
  const inst = spec.inst;
  if (spec.seq) return seqEvents(parseSeq(spec.seq), { bpm: spec.bpm || 90, inst });
  if (spec.harm) {
    const ms = String(spec.harm)
      .trim()
      .split(/\s+/)
      .map((x) => parsePitch(x))
      .filter((x): x is number => x != null);
    return { ev: ms.map((m) => ({ kind: 'note', midi: m, t: 0, dur: 1.9, inst, vel: 0.6 })), length: 2 };
  }
  if (spec.glide) return glideEvents(spec.glide, { bpm: spec.bpm });
  if (spec.chord) return { ev: chordEvents(spec.chord, 0, 2.2, { inst }), length: 2.2 };
  if (spec.prog) {
    const chords = String(spec.prog)
      .trim()
      .split(/\s+/)
      .filter((x) => x !== '|');
    return progEvents(chords, {
      bpm: spec.bpm || 96,
      beats: spec.beats || 4,
      pattern: spec.pattern || (spec.beats === 3 ? 'D-D-D-' : 'D-D-D-D-'),
      inst,
    });
  }
  if (spec.cadence) {
    const c = parseChord(spec.cadence);
    if (!c) return { ev: [], length: 0 };
    return cadenceEvents(c.root, c.q === 'm' ? 'minor' : 'major', { inst, bpm: spec.bpm });
  }
  return { ev: [], length: 0 };
}

export function playSpec(spec: PlaySpec, o: PlayOptions = {}): Player {
  const s = specEvents(spec);
  return play(s.ev, { length: s.length, ...o });
}

export function playGlide(
  points: [number, number][],
  o: { wave?: OscillatorType; vel?: number } & PlayOptions = {},
): Player {
  return play([{ kind: 'glide', t: 0, points, wave: o.wave, vel: o.vel }], {
    length: (points[points.length - 1] as [number, number])[0] + 0.2,
    ...o,
  });
}

// ------------------------------------------------------------ context
export const audioContext = getCtx;
/** Analyser on the final output, for tests that check what the app plays (debug surface only). */
export function outputAnalyser(): AnalyserNode {
  const c = getCtx();
  if (!tap) {
    tap = c.createAnalyser();
    tap.fftSize = 2048;
    output?.connect(tap);
  }
  return tap;
}
export const now = (): number => (ctx ? ctx.currentTime : 0);
export const isReady = (): boolean => !!ctx && ctx.state === 'running';
export const isBusy = (): boolean => !!ctx && ctx.currentTime < busyUntil;
export const getBusyUntil = (): number => busyUntil;

/** Creates (if needed) and resumes the AudioContext. Call from a user gesture. */
export async function resume(): Promise<AudioContext> {
  const c = getCtx();
  if (c.state !== 'running') {
    try {
      await c.resume();
    } catch {
      /* needs a user gesture */
    }
  }
  return c;
}
